import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import SmoothScroll from "@/components/SmoothScroll";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [others, setOthers] = useState([]);

  useEffect(() => {
    document.title = "Loading… — Studio/Saar";
    setProject(null);
    setError(null);
    api.get(`/projects/slug/${slug}`)
      .then((r) => {
        setProject(r.data);
      })
      .catch(() => setError("Project not found"));
    api.get("/projects").then((r) => setOthers(r.data));
    window.scrollTo({ top: 0 });
  }, [slug]);

  const more = others.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <SmoothScroll>
      {project && (
        <SEO
          title={`${project.title} — ${project.category} — Studio/Saar`}
          description={project.description}
          image={project.image_url}
          type="article"
        />
      )}
      {error && <SEO title="Project not found — Studio/Saar" noindex />}
      <div className="grain bg-[#F4F4F0] text-[#0A0A0A] min-h-screen" data-testid="project-detail-page">
        <Navbar />
        <main className="pt-32 md:pt-40">
          {error && (
            <section className="px-6 md:px-12 lg:px-24 py-32" data-testid="project-not-found">
              <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-7xl">Not found.</h1>
              <p className="font-body text-base text-neutral-600 mt-4">No project with slug "{slug}".</p>
              <Link to="/#work" className="inline-block mt-8 link-line font-body text-sm uppercase tracking-[0.25em]" data-testid="back-to-work-link">
                ← Back to all work
              </Link>
            </section>
          )}

          {project && (
            <>
              <header className="px-6 md:px-12 lg:px-24">
                <Link to="/#work" className="inline-block mb-12 link-line font-body text-xs uppercase tracking-[0.25em] text-neutral-500" data-testid="back-to-work-link">
                  ← All work
                </Link>
                <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-6">
                  {project.category} — {project.year}
                </p>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  className="font-display font-black uppercase tracking-tighter text-5xl sm:text-7xl lg:text-[9vw] leading-[0.88]"
                  data-testid="project-detail-title"
                >
                  {project.title}
                </motion.h1>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mt-16 md:mt-24">
                  <div className="md:col-span-3">
                    <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Client</p>
                    <p className="font-body text-base">{project.client || "—"}</p>
                  </div>
                  <div className="md:col-span-3">
                    <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Role</p>
                    <p className="font-body text-base">{project.role || "—"}</p>
                  </div>
                  <div className="md:col-span-3">
                    <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Year</p>
                    <p className="font-body text-base">{project.year}</p>
                  </div>
                  <div className="md:col-span-3">
                    <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Tags</p>
                    <p className="font-body text-base">{(project.tags || []).join(", ") || "—"}</p>
                  </div>
                </div>
              </header>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 }}
                className="mt-20 md:mt-28 img-zoom-wrap mx-6 md:mx-12 lg:mx-24"
              >
                <img src={project.image_url} alt={project.title} className="w-full h-auto" />
              </motion.div>

              <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
                <div className="md:col-span-7 md:col-start-3">
                  <p className="font-display font-bold text-2xl sm:text-3xl leading-snug">
                    {project.description}
                  </p>
                  <p className="font-body text-base text-neutral-700 mt-8 leading-relaxed">
                    Working closely with the client, we approached this project as an
                    exercise in restraint — stripping away convention to reveal what
                    only this brand could say. The result is a system that scales
                    with confidence across every touchpoint.
                  </p>
                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-10 font-display font-bold uppercase tracking-widest text-sm bg-[#0A0A0A] text-[#F4F4F0] px-8 py-5 hover:bg-[#FF3E00] transition-colors duration-300"
                      data-testid="project-external-link"
                    >
                      Visit project →
                    </a>
                  )}
                </div>
              </section>

              {more.length > 0 && (
                <section className="px-6 md:px-12 lg:px-24 pb-32 md:pb-48 border-t border-neutral-300 pt-20">
                  <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-10">More work</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
                    {more.map((p) => (
                      <Link
                        key={p.id}
                        to={`/work/${p.slug}`}
                        className="group"
                        data-testid={`more-project-${p.slug}`}
                        data-cursor="hover"
                      >
                        <div className="img-zoom-wrap aspect-[4/3] bg-neutral-200">
                          <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="mt-6 flex items-baseline justify-between gap-6">
                          <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{p.title}</h3>
                          <span className="font-body text-xs uppercase tracking-[0.2em] text-neutral-500 whitespace-nowrap">{p.year}</span>
                        </div>
                        <p className="font-body text-sm text-neutral-600 mt-2">{p.category}</p>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </main>
        <Footer />
      </div>
    </SmoothScroll>
  );
}
